import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtINR } from "../lib/apiClient";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

const BUCKETS = ["0-30", "31-60", "61-90", "90+"];

export default function PayablesReportPage() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/reports/payables", { timeout: 60000 });
      setData(data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="payables-page">
      <PageHeader title="Payables (Vendor Dues)" subtitle="Vendor-wise outstanding purchase bills with ageing buckets." />
      <div className="p-8 space-y-6">
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Kpi label="Total Payable" value={fmtINR(data.grand.total_due)} accent testId="pay-total-kpi" />
            <Kpi label="Vendors" value={data.grand.vendors} />
            {BUCKETS.map(b => <Kpi key={b} label={`${b} days`} value={fmtINR(data.grand.buckets[b])} warn={b === "90+" && data.grand.buckets[b] > 0} />)}
          </div>
        )}
        {data && (
          <div className="table-wrapper">
            <table className="table-base">
              <thead><tr><th></th><th>Vendor</th><th className="text-right">Bills</th>{BUCKETS.map(b => <th key={b} className="text-right">{b}d</th>)}<th className="text-right">Total Due</th></tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-ink-muted">No vendor dues. All purchase bills are paid.</td></tr>}
                {data.rows.map((v, i) => (
                  <React.Fragment key={v.vendor}>
                    <tr className="cursor-pointer hover:bg-surface-hover" onClick={() => setOpen(o => ({ ...o, [v.vendor]: !o[v.vendor] }))} data-testid={`pay-row-${i}`}>
                      <td className="w-8">{open[v.vendor] ? <CaretDown size={13} /> : <CaretRight size={13} />}</td>
                      <td className="font-semibold">{v.vendor}</td>
                      <td className="text-right tabular">{v.bills.length}</td>
                      {BUCKETS.map(b => <td key={b} className="text-right tabular">{v.buckets[b] > 0 ? fmtINR(v.buckets[b]) : ""}</td>)}
                      <td className="text-right tabular font-bold">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={(e) => { e.stopPropagation(); setPayFor(v); }} className="btn-secondary !py-1 !px-2 text-[11px]" data-testid={`pay-btn-${i}`}>Pay</button>
                          {fmtINR(v.total_due)}
                        </div>
                      </td>
                    </tr>
                    {open[v.vendor] && v.bills.map((b, j) => (
                      <tr key={b.id} className="bg-surface-muted/50" data-testid={`pay-inv-${i}-${j}`}>
                        <td></td>
                        <td className="pl-6 text-xs"><span className="font-mono font-semibold">{b.number}</span> · {b.date} · <span className="status-pill status-neutral">{b.days}d</span></td>
                        <td></td>{BUCKETS.map(k => <td key={k}></td>)}
                        <td className="text-right tabular text-xs">{fmtINR(b.balance)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {data.rows.length > 0 && (
                  <tr className="bg-surface-muted font-bold" data-testid="pay-total-row">
                    <td></td><td>Grand Total</td><td className="text-right tabular">{data.grand.bills}</td>
                    {BUCKETS.map(b => <td key={b} className="text-right tabular">{fmtINR(data.grand.buckets[b])}</td>)}
                    <td className="text-right tabular">{fmtINR(data.grand.total_due)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {payFor && <PayModal vendor={payFor} onClose={() => setPayFor(null)} onDone={() => { setPayFor(null); load(); }} />}
      <FieldStyle />
    </div>
  );
}

function PayModal({ vendor, onClose, onDone }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [mode, setMode] = useState("Bank");
  const [reference, setReference] = useState("");
  const [alloc, setAlloc] = useState(() => Object.fromEntries(vendor.bills.map(b => [b.id, ""])));
  const [saving, setSaving] = useState(false);
  const total = vendor.bills.reduce((s, b) => s + (Number(alloc[b.id]) || 0), 0);

  const save = async () => {
    const allocations = vendor.bills.filter(b => Number(alloc[b.id]) > 0).map(b => ({ target_id: b.id, amount: Number(alloc[b.id]) }));
    if (!allocations.length) { toast.error("Enter an amount against at least one bill"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/vendor-payments/bulk", { payment_date: date, mode, reference, allocations });
      toast.success(`Payment of ${fmtINR(data.total)} recorded against ${data.count} bill(s)`);
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="paybill-modal">
      <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-xl space-y-3 max-h-[85vh] overflow-auto">
        <div className="font-display font-semibold">Bill-wise Payment — {vendor.vendor}</div>
        <div className="grid grid-cols-3 gap-3">
          <div><div className="label-overline mb-1">Date</div><input type="date" className="field" value={date} onChange={e => setDate(e.target.value)} data-testid="pv-date" /></div>
          <div><div className="label-overline mb-1">Mode</div>
            <select className="field" value={mode} onChange={e => setMode(e.target.value)} data-testid="pv-mode">
              {["Bank", "Cash", "UPI", "Cheque"].map(m => <option key={m}>{m}</option>)}
            </select></div>
          <div><div className="label-overline mb-1">Reference</div><input className="field" value={reference} onChange={e => setReference(e.target.value)} data-testid="pv-ref" /></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="label-overline">Allocate against bills</div>
          <button onClick={() => setAlloc(Object.fromEntries(vendor.bills.map(b => [b.id, b.balance])))} className="btn-ghost text-xs" data-testid="pv-autofill">Fill full balances</button>
        </div>
        <div className="space-y-1.5">
          {vendor.bills.map((b, j) => (
            <div key={b.id} className="flex items-center gap-3 border border-border rounded-md px-3 py-2 text-sm">
              <div className="flex-1"><span className="font-mono font-semibold">{b.number}</span><span className="text-[10px] text-ink-muted ml-2">{b.date} · {b.days}d · due {fmtINR(b.balance)}</span></div>
              <input type="number" step="0.01" min="0" placeholder="0.00" className="field !w-32 text-right" value={alloc[b.id]} onChange={e => setAlloc({ ...alloc, [b.id]: e.target.value })} data-testid={`pv-alloc-${j}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t border-border pt-3">
          <div className="font-semibold">Total Payment: <span className="tabular" data-testid="pv-total">{fmtINR(total)}</span></div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="pv-cancel">Cancel</button>
            <button onClick={save} disabled={saving || total <= 0} className="btn-primary" data-testid="pv-save">{saving ? "Saving…" : "Record Payment"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent, warn, testId }) {
  return (
    <div className={`card-base p-3 ${accent ? "bg-brand text-white" : warn ? "bg-destructive text-white" : ""}`} data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-wide ${accent || warn ? "text-white/70" : "text-ink-muted"}`}>{label}</div>
      <div className="font-display text-lg font-bold tabular">{value}</div>
    </div>
  );
}
